/**
 * BadgesManagement — admin back-office for recognition badges.
 *
 * Three jobs:
 *  1. Define badges (create / edit / delete a badge definition).
 *  2. Award a badge to a user. Awarding ALSO auto-issues a downloadable
 *     certificate on the backend (BadgeAdminController → CertificateService
 *     .issueForBadge) in one transaction — the toast confirms both.
 *  3. List every badge definition.
 *
 * The award target is found via the existing user search (`/users/search`),
 * so the admin picks a real user by name/email rather than typing an id.
 */
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'
import { Award, Loader2, AlertCircle, Plus, CheckCircle2, Trash2, Pencil, Search, Ban, Download, FileText } from 'lucide-react'
import {
  listBadgesAdmin, createBadge, updateBadge, deleteBadge, awardBadgeAdmin,
  type BadgeRequest,
} from '@/services/badgeService'
import type { BadgeResponse } from '@/services/communityApi'
import { exportToCSV, exportToPDF, type ExportColumn } from '@/lib/exportUtils'
import userService from '@/services/userService'
import type { User } from '@/types'

const emptyForm: BadgeRequest = { key: '', name: '', description: '', icon: '🏅', criteria: '', tier: 'Bronze' }
const TIER_OPTIONS = ['Bronze', 'Silver', 'Gold']

const badgeExportColumns: ExportColumn<BadgeResponse>[] = [
  { header: 'ID', key: 'id' },
  { header: 'Key', key: 'key' },
  { header: 'Name', key: 'name' },
  { header: 'Tier', key: 'tier' },
  { header: 'Icon', key: 'icon' },
  { header: 'Criteria', key: 'criteria', format: (v) => (v ? String(v) : '') },
  { header: 'Description', key: 'description', format: (v) => (v ? String(v).replace(/\s+/g, ' ').slice(0, 300) : '') },
]

const BadgesManagement = React.memo(() => {
  const { t } = useTranslation()
  const [badges, setBadges] = useState<BadgeResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<{ tone: 'success' | 'error'; msg: string } | null>(null)

  // Create / edit form
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<BadgeRequest>(emptyForm)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)

  // Award panel
  const [selectedBadgeId, setSelectedBadgeId] = useState<number | ''>('')
  const [userQuery, setUserQuery] = useState('')
  const [userResults, setUserResults] = useState<User[]>([])
  const [searching, setSearching] = useState(false)
  const [pickedUser, setPickedUser] = useState<User | null>(null)
  const [awarding, setAwarding] = useState(false)

  const flash = (tone: 'success' | 'error', msg: string) => {
    setToast({ tone, msg })
    setTimeout(() => setToast(null), 3500)
  }

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await listBadgesAdmin()
      setBadges(data)
      if (data.length && selectedBadgeId === '') setSelectedBadgeId(data[0].id)
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load badges.')
    } finally {
      setLoading(false)
    }
  }, [selectedBadgeId])

  useEffect(() => { load() }, [load])

  // Debounced user search (skip very short queries).
  useEffect(() => {
    if (userQuery.trim().length < 2) { setUserResults([]); return }
    let active = true
    setSearching(true)
    const handle = setTimeout(async () => {
      try {
        const res = await userService.searchUsers(userQuery.trim(), 0, 10)
        if (active) setUserResults(res.content ?? [])
      } catch {
        if (active) setUserResults([])
      } finally {
        if (active) setSearching(false)
      }
    }, 350)
    return () => { active = false; clearTimeout(handle) }
  }, [userQuery])

  const set = (k: keyof BadgeRequest, v: string) => setForm((f) => ({ ...f, [k]: v }))

  const openCreate = () => {
    setEditingId(null)
    setForm(emptyForm)
    setFormErrors({})
    setShowForm(true)
  }

  const openEdit = (b: BadgeResponse) => {
    setEditingId(b.id)
    setForm({ key: b.key, name: b.name, description: b.description ?? '', icon: b.icon ?? '🏅', criteria: b.criteria ?? '', tier: b.tier ?? 'Bronze' })
    setFormErrors({})
    setShowForm(true)
  }

  const validate = (): boolean => {
    const errs: Record<string, string> = {}
    if (!editingId && (!form.key || !form.key.trim())) errs.key = 'Key is required (e.g. "tbChampion")'
    if (!form.name.trim()) errs.name = 'Name is required'
    setFormErrors(errs)
    return Object.keys(errs).length === 0
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    setSubmitting(true)
    try {
      if (editingId) {
        await updateBadge(editingId, { ...form, key: undefined })
        flash('success', 'Badge updated')
      } else {
        const created = await createBadge({ ...form, key: form.key!.trim() })
        flash('success', `Badge “${created.name}” created`)
        if (!badges.length) setSelectedBadgeId(created.id)
      }
      setShowForm(false)
      setEditingId(null)
      await load()
    } catch (err: any) {
      flash('error', err?.response?.data?.message || 'Failed to save badge.')
    } finally {
      setSubmitting(false)
    }
  }

  const remove = async (b: BadgeResponse) => {
    if (!window.confirm(`Delete badge “${b.name}”? This only works if it has never been awarded.`)) return
    try {
      await deleteBadge(b.id)
      flash('success', 'Badge deleted')
      await load()
    } catch (err: any) {
      flash('error', err?.response?.data?.message || 'Failed to delete badge.')
    }
  }

  const award = async () => {
    if (!selectedBadgeId || !pickedUser) return
    setAwarding(true)
    try {
      await awardBadgeAdmin(Number(selectedBadgeId), pickedUser.id)
      flash('success', `Badge awarded to ${pickedUser.fullName || pickedUser.firstName || pickedUser.email} and certificate generated.`)
      setPickedUser(null)
      setUserQuery('')
      setUserResults([])
    } catch (err: any) {
      flash('error', err?.response?.data?.message || 'Failed to award badge.')
    } finally {
      setAwarding(false)
    }
  }

  return (
    <GovLayout showSidebar={false}>
      <div className="space-y-6">
        <GovPageTitle title={t('nav.badges', 'Badges')} subtitle="Define recognition badges and award them to users. Awarding a badge auto-generates a downloadable certificate." icon={Award} />

        {toast && (
          <div role="status" className={`flex items-center gap-2 p-3 rounded-lg text-sm ${toast.tone === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            {toast.tone === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />} {toast.msg}
          </div>
        )}

        {/* Award panel */}
        <GovCard>
          <h2 className="text-lg font-bold text-gray-900 mb-3">Award a badge</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="award-badge">Badge</label>
              <select id="award-badge" value={selectedBadgeId} onChange={(e) => setSelectedBadgeId(e.target.value ? Number(e.target.value) : '')}
                className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm">
                {badges.length === 0 && <option value="">No badges defined yet</option>}
                {badges.map((b) => <option key={b.id} value={b.id}>{b.icon} {b.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="award-user">Find user (name or email)</label>
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input id="award-user" value={userQuery} onChange={(e) => { setUserQuery(e.target.value); setPickedUser(null) }}
                  placeholder="Type to search users…" className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-md" />
                {searching && <Loader2 className="w-4 h-4 text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2 animate-spin" />}
              </div>
              {/* Search results */}
              {!pickedUser && userResults.length > 0 && (
                <ul className="mt-1 border border-gray-200 rounded-md max-h-48 overflow-y-auto bg-white shadow-sm">
                  {userResults.map((u) => (
                    <li key={u.id}>
                      <button type="button" onClick={() => { setPickedUser(u); setUserResults([]) }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center justify-between gap-2">
                        <span className="truncate">{u.fullName || u.firstName || u.username}</span>
                        <span className="text-xs text-gray-400 truncate">{u.email}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {pickedUser && (
                <div className="mt-1 flex items-center justify-between gap-2 px-3 py-1.5 bg-blue-50 rounded-md">
                  <span className="text-sm text-blue-800 truncate">→ {pickedUser.fullName || pickedUser.firstName} <span className="text-blue-500">({pickedUser.email})</span></span>
                  <button type="button" onClick={() => setPickedUser(null)} aria-label="Clear" className="text-blue-500 hover:text-blue-700"><Ban className="w-4 h-4" /></button>
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end mt-3">
            <GovButton variant="primary" size="md" onClick={award} disabled={!selectedBadgeId || !pickedUser || awarding}>
              {awarding ? 'Awarding…' : 'Award badge & generate certificate'}
            </GovButton>
          </div>
        </GovCard>

        {/* Create / edit form */}
        <GovCard>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-900">Badge definitions</h2>
            <GovButton variant="primary" size="sm" onClick={showForm ? () => { setShowForm(false); setEditingId(null) } : openCreate}>
              {showForm ? 'Cancel' : 'New badge'}
            </GovButton>
          </div>

          {showForm && (
            <form onSubmit={submit} className="mt-4 grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="b-key">Key {editingId ? '(immutable)' : '*'}</label>
                <input id="b-key" value={form.key} onChange={(e) => set('key', e.target.value)} disabled={!!editingId}
                  className={`w-full border rounded-md px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-400 ${formErrors.key ? 'border-red-400' : 'border-gray-200'}`}
                  placeholder="e.g. tbChampion" maxLength={60} />
                {formErrors.key && <p className="text-xs text-red-600 mt-1">{formErrors.key}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="b-name">Name *</label>
                <input id="b-name" value={form.name} onChange={(e) => set('name', e.target.value)}
                  className={`w-full border rounded-md px-3 py-2 text-sm ${formErrors.name ? 'border-red-400' : 'border-gray-200'}`} maxLength={120} placeholder="e.g. TB Awareness Champion" />
                {formErrors.name && <p className="text-xs text-red-600 mt-1">{formErrors.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="b-icon">Icon (emoji)</label>
                <input id="b-icon" value={form.icon} onChange={(e) => set('icon', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm" maxLength={50} placeholder="🏅" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="b-tier">Tier</label>
                <select id="b-tier" value={form.tier} onChange={(e) => set('tier', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm">
                  {TIER_OPTIONS.map((tier) => <option key={tier} value={tier}>{tier}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="b-criteria">Criteria (short)</label>
                <input id="b-criteria" value={form.criteria} onChange={(e) => set('criteria', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm" maxLength={300} placeholder="e.g. Organised 5+ awareness drives" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="b-desc">Description</label>
                <textarea id="b-desc" value={form.description} onChange={(e) => set('description', e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm" rows={3} maxLength={2000} />
              </div>
              <div className="sm:col-span-2 flex justify-end">
                <GovButton type="submit" variant="primary" disabled={submitting}>
                  {submitting ? 'Saving…' : editingId ? 'Update badge' : 'Create badge'}
                </GovButton>
              </div>
            </form>
          )}
        </GovCard>

        {/* Badge list */}
        <GovCard>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold text-gray-900">All badges</h2>
            <div className="flex items-center gap-2">
              <button onClick={() => exportToCSV('badges', badgeExportColumns, badges)} disabled={!badges.length} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50"><Download className="w-3.5 h-3.5" /> CSV</button>
              <button onClick={() => exportToPDF('badges', 'Badges Report', badgeExportColumns, badges)} disabled={!badges.length} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50"><FileText className="w-3.5 h-3.5" /> PDF</button>
              <button onClick={load} disabled={loading} className="text-xs text-blue-600 hover:underline disabled:opacity-50">{loading ? 'Loading…' : 'Refresh'}</button>
            </div>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-10 text-gray-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
          ) : error ? (
            <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg text-sm"><AlertCircle className="w-4 h-4" /> {error}</div>
          ) : badges.length === 0 ? (
            <GovEmptyState icon="🏅" title="No badges yet" description="Create a badge definition above, then award it to users." />
          ) : (
            <div className="divide-y divide-gray-100">
              {badges.map((b) => (
                <div key={b.id} className="p-4 flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center text-2xl shrink-0">
                    <span aria-hidden="true">{b.icon || '🏅'}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-medium text-gray-900">{b.name}</h4>
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{b.tier}</span>
                      <span className="text-[10px] font-mono text-gray-400">{b.key}</span>
                    </div>
                    {b.criteria && <p className="text-xs text-gray-500 mt-0.5">{b.criteria}</p>}
                    {b.description && <p className="text-sm text-gray-600 mt-1 line-clamp-2">{b.description}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => openEdit(b)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(b)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </GovCard>
      </div>
    </GovLayout>
  )
})

BadgesManagement.displayName = 'BadgesManagement'
export default BadgesManagement